import { EditIcon } from 'assets/icons';
import { LightPlusIcon, PaymentCircleIcon } from 'assets/icons/app';
import OptionItem from 'components/molecules/OptionItem';
import { CheckoutContext } from 'packages/shared/checkout/provider/CheckoutContextProvider';
import type { PaymentCardRef } from 'packages/shared/checkout/types';
import type { SelectPaymentSheetProps } from 'pages/newOrder/modules/payment/selectPaymentSheet';
import SelectPaymentSheet from 'pages/newOrder/modules/payment/selectPaymentSheet';
import { selectCurrentOrder } from 'pages/newOrder/newOrder.selectors';
import { usePaymentCards } from 'pages/queries/payment.queries';
import type { FC, ForwardedRef, SVGProps } from 'react';
import React, { forwardRef, useContext, useImperativeHandle, useMemo } from 'react';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { useSelector } from 'react-redux';
import { hasApplePayPayment } from 'redux-stores/slices/authSlice';
import styled, { type CSSObject, type DefaultTheme } from 'styled-components';

interface IconProps {
  theme: DefaultTheme;
  isActive?: boolean;
  isStroke?: boolean;
  isRemoveStroke?: boolean;
}

const StyledOptionItem = styled(OptionItem)`
  padding-top: 5px;
  padding-bottom: 11px;
  border: unset;
`;

const withIconStyles = (IconComponent: FC<SVGProps<any>>) => styled(IconComponent)<IconProps>`
  > g > path:nth-child(1) {
    ${({ theme, isActive }: IconProps): CSSObject => ({
      fill: isActive ? theme.icon?.activeColor : theme.icon?.color,
    })};
  }

  > g > path:nth-child(2),
  > g > path:nth-child(3) {
    ${({ theme, isActive }: IconProps): CSSObject => ({
      stroke: isActive ? theme.icon?.activeColor : theme.icon?.color,
    })};
  }
`;

const StyledPlusIcon = styled(LightPlusIcon)`
  width: 16px;
  height: 16px;
  margin-right: 5px;
`;

const StyledEditIcon = styled(EditIcon)`
  margin-right: 5px;
`;

const StyledPaymentCircleIcon = withIconStyles(PaymentCircleIcon);

interface Props {}

const DefaultPaymentMethod = (_: Props, ref: ForwardedRef<PaymentCardRef>) => {
  const { paymentMethodRef } = useContext(CheckoutContext)!!;
  const selectPaymentRef = useMemo(() => React.createRef<SelectPaymentSheetProps>(), []);
  const [isPaymentSheetOpen, setIsPaymentSheetOpen] = React.useState(false);
  const { data } = usePaymentCards();
  const { creditCardId } = useSelector(selectCurrentOrder);

  const isApplePay = useSelector(hasApplePayPayment);

  const selectedCard = useMemo(() => data?.cards?.find((c) => c.id === creditCardId), [creditCardId, data?.cards]);

  useImperativeHandle(
    paymentMethodRef ?? ref,
    () => ({
      openNoPaymentMethodSheet: () => {
        selectPaymentRef.current?.toggleSelectPaymentMethodSheet();
      },
    }),
    [selectPaymentRef]
  );

  const SelectedPaymentMethod = useMemo(() => {
    if (isApplePay) {
      return 'Apple Pay';
    }
    if (selectedCard) {
      return `Card ****${selectedCard.last4}`;
    }

    return undefined;
  }, [selectedCard, isApplePay]);

  const handleCardClick = () => {
    // open select payment method sheet
    setIsPaymentSheetOpen(true);
  };

  return (
    <Row className="pb-10">
      <Col>
        <StyledOptionItem
          startIcon={<StyledPaymentCircleIcon isActive={!SelectedPaymentMethod} />}
          title="Payment"
          actionType="edit"
          rightIcon={!SelectedPaymentMethod ? <StyledPlusIcon /> : <StyledEditIcon />}
          isEditable
          hasFixedStyle
          isPreference
          value={SelectedPaymentMethod}
          onClick={handleCardClick}
        />
      </Col>
      <SelectPaymentSheet show={isPaymentSheetOpen} onClose={() => setIsPaymentSheetOpen(false)} ref={selectPaymentRef} />
    </Row>
  );
};

export default forwardRef<PaymentCardRef>(DefaultPaymentMethod);
